package domain.model

import kotlinx.serialization.Serializable

/**
 * GitHub organization model with SSO provider data and membership status
 */
@Serializable
data class GitHubOrganization(
    val id: String,
    val login: String,
    val name: String?,
    val description: String?,
    val avatarUrl: String,
    val htmlUrl: String,
    val location: String?,
    val email: String?,
    val twitterUsername: String?,
    val blog: String?,
    val company: String?,
    val createdAt: String,
    val updatedAt: String,
    val publicRepos: Int,
    val privateRepos: Int,
    val totalPrivateRepos: Int,
    val publicGists: Int,
    val followers: Int,
    val following: Int,
    val type: String,
    // SSO and membership specific fields
    val membership: OrganizationMembership?,
    val ssoProvider: SSOProvider?,
    val requiresSAML: Boolean,
    val isVerified: Boolean,
    val pendingInvitations: List<OrganizationInvitation>
)

/**
 * Organization membership details
 */
@Serializable
data class OrganizationMembership(
    val role: String, // "admin", "member", "billing_manager", "outside_collaborator"
    val state: String, // "active", "pending"
    val url: String,
    val organizationUrl: String,
    val permissions: MembershipPermissions
)

/**
 * Membership permissions within an organization
 */
@Serializable
data class MembershipPermissions(
    val canCreateRepository: Boolean,
    val canCreatePrivateRepository: Boolean,
    val canCreatePublicRepository: Boolean,
    val canCreateTeam: Boolean
)

/**
 * SSO provider configuration
 */
@Serializable
data class SSOProvider(
    val enabled: Boolean,
    val provider: String?, // "saml", "oauth", etc.
    val issuer: String?,
    val loginUrl: String?,
    val certificateFingerprint: String?
)

/**
 * Organization invitation details
 */
@Serializable
data class OrganizationInvitation(
    val id: String,
    val login: String?,
    val email: String?,
    val role: String,
    val teamCount: Int,
    val invitationTeamUrl: String?,
    val invitationUrl: String,
    val createdAt: String,
    val inviter: OrganizationInviter,
    val failedAt: String?,
    val failedReason: String?
)

/**
 * Person who sent the organization invitation
 */
@Serializable
data class OrganizationInviter(
    val login: String,
    val name: String?,
    val avatarUrl: String,
    val htmlUrl: String
)

/**
 * Organization SSO authentication status
 */
@Serializable
data class SSOAuthStatus(
    val isAuthenticated: Boolean,
    val expiresAt: String?,
    val lastAuthenticated: String?,
    val authUrl: String?,
    val requiresReauth: Boolean
)

/**
 * Enhanced organization data combining REST and GraphQL responses
 */
@Serializable
data class OrganizationInsights(
    val organization: GitHubOrganization,
    val ssoStatus: SSOAuthStatus?,
    val teams: List<Team>,
    val recentActivity: OrganizationActivity,
    val accessLevel: OrganizationAccessLevel
)

/**
 * Organization activity summary
 */
@Serializable
data class OrganizationActivity(
    val totalPullRequests: Int,
    val openPullRequests: Int,
    val totalIssues: Int,
    val openIssues: Int,
    val lastActivityAt: String?
)

/**
 * User's access level to the organization
 */
enum class OrganizationAccessLevel {
    OWNER,          // Organization owner
    ADMIN,          // Admin member
    MEMBER,         // Regular member
    OUTSIDE_COLLABORATOR, // Outside collaborator
    NONE,           // No access
    PENDING         // Invitation pending
}
